import { BarChart3, LineChart, PieChart, TrendingUp, Type } from "lucide-react";
import { ChartTypeProps, ItemType, ItemTypeConfig } from "../pages/Dashboard/Dashboard.types";
import { Descendant } from "slate";

// Constants
export const CHART_ITEM_TYPES: Record<string, ChartTypeProps> = {
    LINE_CHART: "LINE_CHART",
    BAR_CHART: "BAR_CHART",
    PIE_CHART: "PIE_CHART",
};

export const ITEM_TYPES: Record<string, ItemType> = {
    NUMBER_CARD: "NUMBER_CARD",
    INDICATOR: "INDICATOR",
    BUTTON: "BUTTON",
    TEXT_EDITOR: "TEXT_EDITOR",
    CHART: "CHART",
};

export const DEFAULT_SIZES: Record<ItemType, ItemTypeConfig> = {
    NUMBER_CARD: { w: 3, h: 3 },
    INDICATOR: { w: 2, h: 2 },
    BUTTON: { w: 2, h: 2 },
    TEXT_EDITOR: { w: 6, h: 6 },
    CHART: { w: 6, h: 4 },
};

export const WIDGET_TYPES = [
    {
        id: "chart",
        name: " Chart",
        description: "Show trends over time",
        icon: LineChart,
        type: ITEM_TYPES.CHART,
    },

    {
        id: "number-card",
        name: "Number Card",
        description: "Display key metrics",
        icon: TrendingUp,
        type: ITEM_TYPES.NUMBER_CARD,
    },
    // {
    //     id: "indicator",
    //     name: "Status Indicator",
    //     description: "Show system status",
    //     icon: Activity,
    //     type: ITEM_TYPES.INDICATOR,
    // },
    // {
    //     id: "button",
    //     name: "Button",
    //     description: "Action trigger",
    //     icon: MousePointer,
    //     type: ITEM_TYPES.BUTTON,
    // },
    {
        id: "text-editor",
        name: "Text Editor",
        description: "Rich text content editor",
        icon: Type,
        type: ITEM_TYPES.TEXT_EDITOR,
    },
];

export const CHART_TYPES_LIST = [
    {
        id: "line-chart",
        name: "Line Chart",
        description: "Show trends over time",
        icon: LineChart,
        type: CHART_ITEM_TYPES.LINE_CHART,
    },
    {
        id: "bar-chart",
        name: "Bar Chart",
        description: "Compare different categories",
        icon: BarChart3,
        type: CHART_ITEM_TYPES.BAR_CHART,
    },
    {
        id: "pie-chart",
        name: "Pie Chart",
        description: "Show proportions",
        icon: PieChart,
        type: CHART_ITEM_TYPES.PIE_CHART,
    },
];

// Chart data sources — each maps to a real API report endpoint
export const CHART_DATA_SOURCES = [
    { value: "deals-by-stage", label: "Deals by Stage" },
    { value: "deals-by-month-count", label: "Deals by Month (Count)" },
    { value: "deals-by-month-value", label: "Deals by Month (Value $)" },
    { value: "pipeline-funnel", label: "Pipeline Funnel" },
    { value: "revenue-forecast", label: "Revenue Forecast" },
    { value: "win-loss-won", label: "Deals Won by Month" },
    { value: "win-loss-lost", label: "Deals Lost by Month" },
    { value: "value-distribution", label: "Deal Value Distribution" },
    { value: "activities-by-type", label: "Activities by Type" },
    { value: "activities-by-user", label: "Activities by User" },
    { value: "contact-growth", label: "Contact Growth (Monthly)" },
    { value: "companies-by-industry", label: "Companies by Industry" },
];

// KPI (single-value) data sources — used by Number Card widgets
export const KPI_DATA_SOURCES = [
    { value: "kpi-total-pipeline", label: "Total Pipeline Value" },
    { value: "kpi-active-contacts", label: "Active Contacts" },
    { value: "kpi-deals-won-month", label: "Deals Won This Month (Count)" },
    { value: "kpi-deals-won-month-value", label: "Deals Won This Month (Value $)" },
    { value: "kpi-activities-week", label: "Activities This Week" },
];

// Legacy alias
export const DATA_SOURCES = CHART_DATA_SOURCES;

export const SAMPLE_DATA = [
    {
        id: "b43e07ac-1fab-4e52-9c6f-de3657b6c556",
        data: Array.from({ length: 7 }, (_, i) => ({
            name: `Day ${i + 1}`,
            value: Math.floor(Math.random() * 100),
        })),
    },
    {
        id: "5a8c4bfe-8414-47e0-ba30-891d6b6677a4",
        data: Array.from({ length: 12 }, (_, i) => ({
            name: `Month ${i + 1}`,
            value: Math.floor(Math.random() * 100),
        })),
    },
    {
        id: "8b9a97ed-e377-4fd6-a302-e167e6b98284",
        data: 23988,
    },
    {
        id: "990b4523-d069-4147-8e5f-e9d57ac6ee14",
        data: [
            { name: "Jan", value: 400 },
            { name: "Feb", value: 300 },
        ],
    },
];

export const TEXT_EDITOR_DEFAULT_VALUE: Descendant[] = [
    {
        type: "paragraph",
        align: "left",
        children: [{ text: "" }],
    },
];

export const DEAL_STAGES = {
    PROSPECTING: "prospecting",
    QUALIFICATION: "qualification",
    PROPOSAL: "proposal",
    NEGOTIATION: "negotiation",
    CLOSED_WON: "closed-won",
    CLOSED_LOST: "closed-lost",
} as const;
export const PRIORITY_LEVELS = ["low", "medium", "high", "urgent"];

export const DEAL_STAGE_OPTIONS = [
    { value: "prospecting", label: "Prospecting" },
    { value: "qualification", label: "Qualification" },
    { value: "proposal", label: "Proposal" },
    { value: "negotiation", label: "Negotiation" },
    { value: "closed-won", label: "Closed Won" },
    { value: "closed-lost", label: "Closed Lost" },
];

export const PRIORITY_LEVEL_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
];

export const STATUS_OPTIONS = [
    { value: "open", label: "Open" },
    { value: "in-progress", label: "In Progress" },
    { value: "closed", label: "Closed" },
];

export const SORT_OPTIONS = [
    { value: "createdAt", label: "Created At" },
    { value: "updatedAt", label: "Updated At" },
    { value: "dealValue", label: "Deal Value" },
    { value: "customerName", label: "Customer Name" },
];

export const NUMBER_CARD_FORMAT_OPTIONS = [
    { value: "number", label: "Number" },
    { value: "currency", label: "Currency" },
    { value: "percentage", label: "Percentage" },
];

export const ACTIVITY_TYPE_OPTIONS = [
    { value: "call", label: "Call" },
    { value: "email", label: "Email" },
    { value: "meeting", label: "Meeting" },
    { value: "task", label: "Task" },
    { value: "note", label: "Note" },
    { value: "deadline", label: "Deadline" },
];

export const ACTIVITY_STATUS_OPTIONS = [
    { value: "OPEN", label: "Open" },
    { value: "DONE", label: "Done" },
];

export const ACTIVITY_OUTCOME_OPTIONS = [
    { value: "completed", label: "Completed" },
    { value: "no-answer", label: "No Answer" },
    { value: "left-voicemail", label: "Left Voicemail" },
    { value: "rescheduled", label: "Rescheduled" },
];

export const DEAL_STATUS_OPTIONS = [
    { value: "OPEN", label: "Open" },
    { value: "WON", label: "Won" },
    { value: "LOST", label: "Lost" },
];

export const DEAL_LOST_REASON_OPTIONS = [
    { value: "price", label: "Price" },
    { value: "competition", label: "Competition" },
    { value: "timing", label: "Timing" },
    { value: "no-budget", label: "No Budget" },
    { value: "no-decision", label: "No Decision" },
    { value: "product-fit", label: "Product Fit" },
    { value: "other", label: "Other" },
];

export const LEAD_SOURCE_OPTIONS = [
    { value: "website", label: "Website" },
    { value: "referral", label: "Referral" },
    { value: "cold-call", label: "Cold Call" },
    { value: "social-media", label: "Social Media" },
    { value: "event", label: "Event" },
    { value: "partner", label: "Partner" },
    { value: "advertising", label: "Advertising" },
    { value: "other", label: "Other" },
];

export const CONTACT_STATUS_OPTIONS = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "bounced", label: "Bounced" },
    { value: "unsubscribed", label: "Unsubscribed" },
];

export const PREFERRED_CONTACT_METHOD_OPTIONS = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "mobile", label: "Mobile" },
];

export const COMPANY_STATUS_OPTIONS = [
    { value: "prospect", label: "Prospect" },
    { value: "active", label: "Active" },
    { value: "churned", label: "Churned" },
    { value: "inactive", label: "Inactive" },
];

export const INDUSTRY_OPTIONS = [
    { value: "technology", label: "Technology" },
    { value: "finance", label: "Finance" },
    { value: "healthcare", label: "Healthcare" },
    { value: "retail", label: "Retail" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "education", label: "Education" },
    { value: "real-estate", label: "Real Estate" },
];
